<template>
    <section class="delivery-hours-editor" :class="{ 'mt-4': !hideTitle }">
        <h6 v-if="!hideTitle" class="text-overline font-weight-bold">{{ $translate('title') }}</h6>
        <v-form ref="form">
            <div
                v-for="(day, index) in days"
                :key="day.day"
                class="delivery-hours-day"
                :class="{ 'delivery-hours-day--first': !index }"
            >
                <v-row dense align="center" class="stitch-modal__fields">
                    <v-col cols="12" sm="2" class="font-weight-bold delivery-hours-day__name">{{ weekday(day.day) }}</v-col>
                    <v-col cols="12" sm="7">
                        <v-select v-model="day.mode" :items="modes" :aria-label="weekday(day.day)" dense outlined hide-details="auto" :disabled="readonly" @change="changeMode(day)" />
                    </v-col>
                    <v-col cols="12" sm="3" v-if="index && !readonly">
                        <v-btn text small class="text-none delivery-hours-day__copy" @click="copyPrevious(index)">{{ $translate('copy') }}</v-btn>
                    </v-col>
                </v-row>
                <v-row v-if="day.mode === 'slots'" dense class="delivery-hours-day__slots">
                    <v-col cols="12" sm="7" offset-sm="2">
                    <v-row v-for="(slot, slotIndex) in day.slots" :key="slotIndex" dense align="start" class="stitch-modal__fields">
                        <v-col cols="5">
                            <v-text-field v-model="slot.start" type="time" :label="$translate('from')" :rules="[required]" dense outlined hide-details="auto" :disabled="readonly" @input="emitValue" />
                        </v-col>
                        <v-col cols="5">
                            <v-text-field v-model="slot.end" type="time" :label="$translate('to')" :rules="[required, () => validSlot(day, slot)]" dense outlined hide-details="auto" :disabled="readonly" @input="emitValue" />
                        </v-col>
                        <v-col cols="2" v-if="!readonly">
                            <v-btn class="delivery-hours-day__remove" icon :aria-label="$translate('remove')" @click="removeSlot(day, slotIndex)"><v-icon small>mdi-close</v-icon></v-btn>
                        </v-col>
                    </v-row>
                    <v-btn v-if="!readonly && day.slots.length < 12" text small color="primary" class="text-none mt-1" @click="addSlot(day)">{{ $translate('add') }}</v-btn>
                    </v-col>
                </v-row>
            </div>
        </v-form>
    </section>
</template>
<script>
import GlobalMixins from '@/mixins/global';
export default {
    mixins: [GlobalMixins],
    props: {
        value: { type: Object, default: null },
        readonly: Boolean,
        hideTitle: { type: Boolean, default: false },
    },
    data: () => ({ days: [] }),
    watch: {
        value: {
            immediate: true,
            handler(value) {
                this.days = Array.from({ length: 7 }, (_, index) => {
                    const source = value?.days?.find(day => day.day === index + 1);
                    return { day: index + 1, mode: source?.mode === 'unset' ? 'unknown' : (source?.mode || 'unknown'), slots: JSON.parse(JSON.stringify(source?.slots || [])) };
                });
            },
        },
    },
    computed: {
        modes() { return ['unknown', 'closed', 'appointment', 'slots'].map(value => ({ value, text: this.$translate(value) })); },
    },
    methods: {
        weekday(day) { return new Date(2024, 0, day).toLocaleDateString(this.$store.state.session.active_locale || 'fr', { weekday: 'long' }); },
        required(value) { return !!value || this.$translate('required'); },
        validSlot(day, slot) {
            return (slot.end > slot.start && !day.slots.some(other => other !== slot && other.start < slot.end && other.end > slot.start)) || this.$translate('invalid');
        },
        validate() { return this.$refs.form.validate(); },
        emitValue() { this.$emit('input', { days: JSON.parse(JSON.stringify(this.days)) }); },
        changeMode(day) { day.slots = day.mode === 'slots' ? [{ start: '', end: '' }] : []; this.emitValue(); },
        addSlot(day) { day.slots.push({ start: '', end: '' }); this.emitValue(); },
        removeSlot(day, index) {
            day.slots.splice(index, 1);
            if (!day.slots.length) day.mode = 'unknown';
            this.emitValue();
        },
        copyPrevious(index) { this.$set(this.days, index, { ...JSON.parse(JSON.stringify(this.days[index - 1])), day: index + 1 }); this.emitValue(); },
    },
    i18n: { messages: {
        fr: { title: 'Horaires de livraison', unknown: 'Non renseigné', closed: 'Fermé', appointment: 'Sur rendez-vous', slots: 'Créneaux de livraison', copy: 'Copier la veille', from: 'Début', to: 'Fin', add: 'Ajouter un créneau', remove: 'Supprimer le créneau', required: 'Champ requis', invalid: 'La fin doit suivre le début, sans chevauchement.' },
        en: { title: 'Delivery hours', unknown: 'Not provided', closed: 'Closed', appointment: 'By appointment', slots: 'Delivery slots', copy: 'Copy previous day', from: 'Start', to: 'End', add: 'Add a slot', remove: 'Remove slot', required: 'Required', invalid: 'End must follow start, without overlapping slots.' },
    } },
};
</script>

<style scoped>
.delivery-hours-day {
    padding: 12px 16px;
    margin-bottom: 10px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #f8fafc;
}

.delivery-hours-day--first {
    margin-top: 0;
}

.delivery-hours-day__name { text-transform: capitalize; color: #334155; }
.delivery-hours-day__slots { margin-top: 4px; }
.delivery-hours-day__copy { letter-spacing: normal; }

.delivery-hours-editor .delivery-hours-day ::v-deep .v-text-field--outlined > .v-input__control > .v-input__slot {
    background: #fff !important;
}

.stitch-form .delivery-hours-day__remove {
    margin-top: 22px;
}
.delivery-hours-day__slots ::v-deep .v-messages__message {
    line-height: 1.4;
}
</style>
